import React, { useCallback, useState, useMemo } from 'react';
import { WMButton, WMButtonVariant, WMTextField } from '@walkme/wm-ui';
import { create, InstanceProps } from 'react-modal-promise';
import { useSelector } from '../../../infrastructure/store.interface';
import StyledWMDialog from '../../Dialogs/StyledWMDialog/StyledWMDialog';
import { AudienceRulesEditor } from './AudienceRulesEditor/AudienceRulesEditor';
import { ruleTemplate, hasRulesChanged } from './utils';
import * as yup from 'yup';
import _omit from 'lodash/omit';
import { yupResolver } from '@hookform/resolvers/yup';
import classes from './CreateAudienceDialog.module.scss';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { AudiencesTabs } from '../../../state/reducers/audiences';
import { getErrorMessageHandlerProps, useCreateAudienceMutation, useUpdateAudienceMutation } from '../hooks/hooks';
import { AudienceGraphql, AudienceRuleGraphql, AudienceRuleOperatorEnum } from '../../../../generated/apollo-hooks';
import { useTranslation } from 'react-i18next';

import { ApolloError } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { audiencesActions } from '../../../state/actionTypes/audiences';
import AudienceValidationErrorDialog from './AudienceValidationErrorDialog/AudienceValidationErrorDialog';
import { TableKeys } from '@communication-center/data-objects';
import { TableKeys as Tab, TabsConfig } from '../../../models';
import { setActiveTab, setTableState } from '../../../state/actions/notificationsList';
import { enqueueSnackbar } from '../../../state/actions/ui';
import { useNotificationsFilterFields } from '../../NotificationsList/hooks/notifications';
import { Locations } from '../../../models/locations';
import RecipientsData from './RecipientsData/RecipientsData';
import NotEditablePrompt from './NotEditablePrompt/NotEditablePrompt';
import AffectedNotificationsPrompt from './AffectedNotificationsPrompt/AffectedNotificationsPrompt';
import { CreateAudienceDialogContext, ICreateAudienceDialogContext } from './CreateAudienceDialogContext';
import { useWalkmeSdkService } from '../../../infrastructure/WalkmeSdkProvider/useWalkmeSdkService';
import NeedToChangeRulesBeforeSavePrompt from './NeedToChangeRulesBeforeSavePrompt/NeedToChangeRulesBeforeSavePrompt';
import { useLocation } from 'react-router-dom';

const keyPrefix = 'components.audiences.createAudienceDialog.createAudienceDialog';

interface ICreateAudienceDialogProps extends InstanceProps<AudienceGraphql> {
    audience?: ICreateAudienceDialogContext['audience'];
    audienceRules?: AudienceRuleGraphql[];
    duplicateMode?: boolean;
}

const dataTestId = 'create-audience-dialog';

const validRules = (rules: AudienceRuleGraphql[]) => rules.filter(rule => !!rule.values.length).map(rule => _omit(rule, 'integrationType', 'role'));

export type AudienceFormFields = {
    title: string;
    description: string;
    rules: AudienceRuleGraphql[];
};

const isEditMode = (audience?: ICreateAudienceDialogProps['audience']): audience is AudienceGraphql => {
    return !!audience && 'id' in audience;
};

const CreateAudienceDialog = ({ onResolve, isOpen, audience, audienceRules, close, duplicateMode }: ICreateAudienceDialogProps) => {
    const { t } = useTranslation('', { keyPrefix });
    const { pathname } = useLocation();
    const editMode = isEditMode(audience);
    const user = useSelector(store => store.common.user);
    const notificationsFilterFields = useNotificationsFilterFields();
    const { walkmeSdkService } = useWalkmeSdkService();

    const defaultRules = audienceRules ?? [ruleTemplate];

    const EMPTY_ERROR = useMemo(() => t('emptyError'), [t]);

    const schema = useMemo(
        () =>
            yup
                .object({
                    title: yup.string().max(80, t('schema.title')).required(EMPTY_ERROR),
                    description: yup.string().max(200, t('schema.description')),
                    rules: yup
                        .array()
                        .of(
                            yup
                                .object({
                                    alias: yup.string(),
                                    integrationId: yup.string().nullable(),
                                    property: yup.string().required(EMPTY_ERROR),
                                    operator: yup.string().nullable(),
                                    values: yup
                                        .array()
                                        .when('property', {
                                            is: (property: $TSFixMe) => !property?.length,
                                            then: yup.array(),
                                            otherwise: yup.array().when('operator', {
                                                is: (operator: string) => {
                                                    return operator === AudienceRuleOperatorEnum.Contains;
                                                },
                                                then: yup.array().min(1, EMPTY_ERROR),
                                                otherwise: yup.array().min(1, t('schema.otherwise')).required()
                                            })
                                        })
                                        .required()
                                })
                                .required(EMPTY_ERROR)
                        )
                        .required(t('schema.required'))
                        .min(1, t('schema.min'))
                })
                .required(),
        [EMPTY_ERROR, t]
    );

    const useFormMethods = useForm<AudienceFormFields>({
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        defaultValues: {
            title: audience?.title ?? '',
            description: audience?.description ?? '',
            rules: defaultRules
        },
        resolver: yupResolver(schema)
    });

    const {
        control,
        handleSubmit,
        register,
        getValues,
        formState: { errors, isDirty, dirtyFields },
        setError
    } = useFormMethods;
    const dispatch = useDispatch();
    const [calculatedRules, setCalculatedRules] = useState<AudienceRuleGraphql[]>(validRules(getValues('rules')));

    const onError = useCallback(
        (error: any) => {
            const apolloError = error as ApolloError;
            const { showDialog, msg, showSnackBar, isBusinessLogicError } = getErrorMessageHandlerProps({ error: apolloError, t });
            if (!showDialog) {
                setError('title', { ...apolloError, message: msg });
            } else {
                AudienceValidationErrorDialog.openModal({
                    audienceName: msg.title,
                    closeParent: close,
                    archivedAudience: msg.archivedAudience
                } as $TSFixMe);
            }
            setIsSaving(false);
            return { showSnackBar, isBusinessLogicError };
        },
        [close, setError, t]
    );
    const updateAudienceMutation = useUpdateAudienceMutation(onError);
    const createAudienceMutation = useCreateAudienceMutation(onError);
    const [descriptionShow, setDescriptionShow] = useState(!!getValues('description'));
    const [isSaving, setIsSaving] = useState(false);
    const [selectPropertyAutofocus, setSelectPropertyAutofocus] = useState(!!audience?.title && !editMode && !duplicateMode);

    const hasNotifications = audience && 'notificationsNumber' in audience && (audience.notificationsNumber ?? 0) > 0;
    const hasAffectedNotifications =
        hasNotifications &&
        'notificationsStatus' in audience! &&
        ((audience.notificationsStatus?.[TableKeys.DRAFTS] ?? 0) > 0 || (audience.notificationsStatus?.[TableKeys.SCHEDULED] ?? 0) > 0);

    const hasActiveNotifications =
        hasNotifications && 'notificationsStatus' in audience! && (audience.notificationsStatus?.[TableKeys.ACTIVE] ?? 0) > 0;

    const calculateRecipientsSize = useCallback(async () => {
        const { rules: _rules } = getValues();
        const rules = validRules(_rules);
        setCalculatedRules(rules);
    }, [getValues]);

    const saveAudience = useCallback(async () => {
        if (isSaving) {
            return;
        }

        setIsSaving(true);
        const { title, description, rules: _rules } = getValues();
        const rules = validRules(_rules);

        try {
            const body = {
                title,
                description,
                data: { rules },
                user
            };

            const data = await (isEditMode(audience)
                ? updateAudienceMutation(
                      Object.assign(body, {
                          id: audience.id,
                          size: audience.size,
                          sizeTotal: audience.sizeTotal!,
                          sizeInactive: audience.sizeInactive!
                      })
                  )
                : createAudienceMutation(body));

            dispatch({ type: audiencesActions.SET_CURRENT_TAB, payload: AudiencesTabs.Active });
            onResolve(data!);
        } catch (error) {
            setIsSaving(false);
        }
    }, [isSaving, getValues, audience, user, updateAudienceMutation, createAudienceMutation, onResolve, dispatch]);

    const redirectToNotification = useCallback(
        async (tab: Tab): Promise<void> => {
            if (!isEditMode(audience)) {
                return;
            }

            const exist = notificationsFilterFields?.[tab]?.audiences?.some?.((audiencesId: string) => audiencesId === audience.id);

            if (exist) {
                dispatch(setTableState(TabsConfig[tab].tableKey, { filters: { audiencesIds: [audience.id] } }));
            } else {
                dispatch(
                    enqueueSnackbar({
                        message: t('snackbar'),
                        options: { variant: 'error' }
                    })
                );
            }
            dispatch(setActiveTab(tab));

            if (pathname !== Locations.Audiences) {
                close();
            }

            await walkmeSdkService.changeRoute(Locations.Root);
        },
        [audience, notificationsFilterFields, dispatch, t, pathname, close, walkmeSdkService]
    );

    const onSubmitFn = handleSubmit(saveAudience);

    const context = useMemo(
        () => ({
            audience,
            dataTestId,
            redirectToNotification,
            rules: calculatedRules,
            editMode
        }),
        [audience, redirectToNotification, calculatedRules, editMode]
    );

    return (
        <CreateAudienceDialogContext.Provider value={context}>
            <FormProvider {...useFormMethods}>
                <StyledWMDialog
                    title={editMode ? t('editAudience') : t('createAudience')}
                    open={isOpen}
                    onClose={() => onResolve()}
                    buttons={[
                        {
                            children: t('cancel'),
                            variant: WMButtonVariant.Secondary,
                            className: classes.cancelButton,
                            disabled: isSaving,
                            onClick: () => onResolve(undefined),
                            ['data-testid' as $TSFixMe]: `${dataTestId}-discard`
                        },
                        {
                            children: duplicateMode ? t('saveAsNew') : editMode ? t('save') : t('create'),
                            variant: WMButtonVariant.Primary,
                            loading: isSaving,
                            disabled:
                                !isDirty ||
                                (!dirtyFields?.title &&
                                    !dirtyFields?.description &&
                                    !!dirtyFields?.rules &&
                                    !hasRulesChanged(defaultRules, getValues().rules)) ||
                                (hasActiveNotifications && !!dirtyFields?.rules && hasRulesChanged(defaultRules, getValues().rules)) ||
                                (duplicateMode && !hasRulesChanged(defaultRules, getValues().rules)),
                            type: 'submit',
                            ['data-testid' as $TSFixMe]: `${dataTestId}-confirm`
                        }
                    ]}
                    className={classes.dialogRoot}
                    classes={{ content: classes.dialogContent }}
                    data-testid={dataTestId}
                    onSubmit={onSubmitFn}
                    PaperComponent={'form' as $TSFixMe}
                >
                    <div className={classes.audienceContainer}>
                        <div className={classes.audienceContent}>
                            <div className={classes.propmtsMessages}>
                                {hasActiveNotifications && <NotEditablePrompt />}
                                {hasAffectedNotifications && <AffectedNotificationsPrompt />}
                                {duplicateMode && !hasRulesChanged(defaultRules, getValues().rules) && <NeedToChangeRulesBeforeSavePrompt />}
                            </div>

                            <Controller
                                control={control}
                                name="title"
                                render={({ field: { value, onChange } }) => (
                                    <WMTextField
                                        className={classes.addTitle}
                                        InputLabelProps={{ classes: { root: classes.addTitleLabel } }}
                                        label={t('audienceName.label')}
                                        placeholder={t('audienceName.placeholder')}
                                        value={value}
                                        disabled={isSaving}
                                        onChange={onChange}
                                        inputProps={{
                                            'data-testid': `${dataTestId}-title-input`
                                        }}
                                        error={errors?.title?.message}
                                        data-testid={`${dataTestId}-title`}
                                        onFocus={() => setSelectPropertyAutofocus(true)}
                                        autoFocus
                                    />
                                )}
                            />

                            {!descriptionShow && (
                                <WMButton
                                    variant={WMButtonVariant.Text}
                                    onClick={() => setDescriptionShow(true)}
                                    disabled={isSaving}
                                    data-testid={`${dataTestId}-show-description-button`}
                                >
                                    <span className={classes.addDescription}>{t('description.addDescription')}</span>
                                </WMButton>
                            )}

                            {descriptionShow && (
                                <Controller
                                    control={control}
                                    name="description"
                                    render={({ field: { value, onChange } }) => (
                                        <WMTextField
                                            className={classes.descField}
                                            value={value}
                                            onChange={onChange}
                                            label={t('description.label')}
                                            disabled={isSaving}
                                            placeholder={t('description.placeholder')}
                                            multiline
                                            inputProps={{
                                                'data-testid': `${dataTestId}-description-input`,
                                                ...register('description')
                                            }}
                                            error={errors?.description?.message}
                                            minRows={3}
                                            data-testid={`${dataTestId}-description`}
                                        />
                                    )}
                                />
                            )}

                            <div className={classes.rulesContainer}>
                                <div className={classes.rulesHeader}>
                                    <span className={classes.rulesTitle}>{t('rulesContainer.title')}</span>
                                    <>
                                        <span className={classes.rulesDescription}>
                                            {editMode ? t('rulesContainer.descriptionEdit') : t('rulesContainer.descriptionCreate')}
                                        </span>
                                        <div className={classes.rulesSecondaryDescription}>{t('rulesContainer.secondaryDescription')}</div>
                                    </>
                                </div>

                                <AudienceRulesEditor
                                    disabled={isSaving}
                                    hasNotifications={hasActiveNotifications}
                                    calculateRecipientsSize={calculateRecipientsSize}
                                    selectPropertyAutofocus={selectPropertyAutofocus}
                                />
                            </div>
                        </div>
                    </div>
                    <RecipientsData />
                </StyledWMDialog>
            </FormProvider>
        </CreateAudienceDialogContext.Provider>
    );
};

CreateAudienceDialog.openModal = create(CreateAudienceDialog);

export default CreateAudienceDialog;
